import axios, { isAxiosError } from "axios";
import {
  Flow,
  Node,
  Handle,
  Edge,
  FlowTipo,
} from "../../context/FluxogramaContext";
import { RequestError } from "./api";
import { TableNodeStyle } from "../../components";
import { BarragemFluxogramas } from "../../context";

const BASE_URL = (barragemId: number) =>
  `${process.env.REACT_APP_URL}/barragens/${barragemId}/fluxogramas`;

export type HandleType = "source" | "target";
export type HandlePosition = "left" | "right" | "top" | "bottom";

const getAll = async (
  barragemId: number
): Promise<BarragemFluxogramas | RequestError> => {
  try {
    const { data } = await axios.get<{
      "Nível de alerta": number | undefined;
      "Nível 1": number | undefined;
      "Nível 2": number | undefined;
      "Nível 3": number | undefined;
    }>(`${BASE_URL(barragemId)}/`);
    return {
      NV0: data["Nível de alerta"] ?? null,
      NV1: data["Nível 1"] ?? null,
      NV2: data["Nível 2"] ?? null,
      NV3: data["Nível 3"] ?? null,
    };
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Fluxograma Error",
        message: error.response?.data.message ?? "Erro ao obter fluxograma",
      };
    }
    return {
      error: "Fluxograma Error",
      message: "Erro ao obter fluxograma",
    };
  }
};

const addFlow = async (
  name: string,
  tipo: FlowTipo,
  barragemId: number,
  style?: React.CSSProperties
): Promise<Flow | RequestError> => {
  try {
    const { data } = await axios.post(`${BASE_URL(barragemId)}/`, {
      name,
      tipo,
      style,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Fluxograma Error",
        message: error.response?.data.message ?? "Erro ao cadastrar fluxograma",
      };
    }
    return {
      error: "Fluxograma Error",
      message: "Erro ao cadastrar fluxograma",
    };
  }
};

const updateFlow = async (
  barragemId: number,
  id: number,
  fields: {
    name?: string;
    style?: React.CSSProperties;
    tipo?: FlowTipo;
  }
): Promise<Flow | RequestError> => {
  try {
    const { data } = await axios.put<Flow>(
      `${BASE_URL(barragemId)}/${id}`,
      fields
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Fluxograma Error",
        message: error.response?.data.message ?? "Erro ao atualizar fluxograma",
      };
    }
    return {
      error: "Fluxograma Error",
      message: "Erro ao atualizar fluxograma",
    };
  }
};

const deleteFlow = async (
  barragemId: number,
  id: number
): Promise<Flow | RequestError> => {
  try {
    const { data } = await axios.delete<Flow>(`${BASE_URL(barragemId)}/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Fluxograma Error",
        message: error.response?.data.message ?? "Erro ao remover fluxograma",
      };
    }
    return {
      error: "Fluxograma Error",
      message: "Erro ao remover fluxograma",
    };
  }
};

const getFlow = async (
  barragemId: number,
  id: number
): Promise<Flow | RequestError> => {
  try {
    const { data } = await axios.get<Flow>(`${BASE_URL(barragemId)}/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Fluxograma Error",
        message: error.response?.data.message ?? "Erro ao obter fluxograma",
      };
    }
    return {
      error: "Fluxograma Error",
      message: "Erro ao obter fluxograma",
    };
  }
};

// --- Node ---
const addNode = async (
  barragemId: number,
  flowId: number,
  posX: number,
  posY: number,
  markdown?: string,
  style?: React.CSSProperties | TableNodeStyle,
  id?: string
): Promise<Node | RequestError> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = { posX, posY, markdown, style };
    if (id) payload.id = id;

    const { data } = await axios.post<Node>(
      `${BASE_URL(barragemId)}/${flowId}/nodes`,
      payload
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Node Error",
        message: error.response?.data.message ?? "Erro ao cadastrar node",
      };
    }
    return {
      error: "Node Error",
      message: "Erro ao cadastrar node",
    };
  }
};

const updateNode = async (
  barragemId: number,
  flowId: number,
  nodeId: string,
  fields: {
    posX?: number;
    posY?: number;
    markdown?: string;
    style?: React.CSSProperties | TableNodeStyle;
  }
): Promise<Node | RequestError> => {
  try {
    const { data } = await axios.put<Node>(
      `${BASE_URL(barragemId)}/${flowId}/nodes/${nodeId}`,
      fields
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Node Error",
        message: error.response?.data.message ?? "Erro ao atualizar node",
      };
    }
    return {
      error: "Node Error",
      message: "Erro ao atualizar node",
    };
  }
};

const deleteNode = async (
  barragemId: number,
  flowId: number,
  nodeId: string
): Promise<Node | RequestError> => {
  try {
    const { data } = await axios.delete<Node>(
      `${BASE_URL(barragemId)}/${flowId}/nodes/${nodeId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Node Error",
        message: error.response?.data.message ?? "Erro ao remover node",
      };
    }
    return {
      error: "Node Error",
      message: "Erro ao remover node",
    };
  }
};

const getNode = async (
  barragemId: number,
  flowId: number,
  nodeId: string
): Promise<Node | RequestError> => {
  try {
    const { data } = await axios.get<Node>(
      `${BASE_URL(barragemId)}/${flowId}/nodes/${nodeId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Node Error",
        message: error.response?.data.message ?? "Erro ao obter node",
      };
    }
    return {
      error: "Node Error",
      message: "Erro ao obter node",
    };
  }
};

// --- Edge ---
const addEdge = async (
  barragemId: number,
  flowId: number,
  handleSourceId: string,
  handleTargetId: string,
  markdown?: string,
  style?: React.CSSProperties,
  id?: string
): Promise<Edge | RequestError> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      handleSourceId,
      handleTargetId,
      markdown,
      style,
    };

    if (id) {
      payload.id = id;
    }

    const { data } = await axios.post<Edge>(
      `${BASE_URL(barragemId)}/${flowId}/edges`,
      payload
    );

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Edge Error",
        message: error.response?.data.message ?? "Erro ao cadastrar edge",
      };
    }
    return {
      error: "Edge Error",
      message: "Erro ao cadastrar edge",
    };
  }
};

const updateEdge = async (
  barragemId: number,
  flowId: number,
  edgeId: string,
  fields: {
    handleSourceId?: string;
    handleTargetId?: string;
    markdown?: string;
    style?: React.CSSProperties;
  }
): Promise<Edge | RequestError> => {
  try {
    const { data } = await axios.put<Edge>(
      `${BASE_URL(barragemId)}/${flowId}/edges/${edgeId}`,
      fields
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Edge Error",
        message: error.response?.data.message ?? "Erro ao atualizar edge",
      };
    }
    return {
      error: "Edge Error",
      message: "Erro ao atualizar edge",
    };
  }
};

const deleteEdge = async (
  barragemId: number,
  flowId: number,
  edgeId: string
): Promise<Edge | RequestError> => {
  try {
    const { data } = await axios.delete<Edge>(
      `${BASE_URL(barragemId)}/${flowId}/edges/${edgeId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Edge Error",
        message: error.response?.data.message ?? "Erro ao remover edge",
      };
    }
    return {
      error: "Edge Error",
      message: "Erro ao remover edge",
    };
  }
};

const getEdge = async (
  barragemId: number,
  flowId: number,
  edgeId: string
): Promise<Edge | RequestError> => {
  try {
    const { data } = await axios.get<Edge>(
      `${BASE_URL(barragemId)}/${flowId}/edges/${edgeId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Edge Error",
        message: error.response?.data.message ?? "Erro ao obter edge",
      };
    }
    return {
      error: "Edge Error",
      message: "Erro ao obter edge",
    };
  }
};

// --- Handle ---
const addHandle = async (
  barragemId: number,
  flowId: number,
  nodeId: string,
  type: HandleType,
  position: HandlePosition,
  style?: React.CSSProperties,
  id?: string
): Promise<Handle | RequestError> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = { type, position, style };
    if (id) payload.id = id;

    const { data } = await axios.post<Handle>(
      `${BASE_URL(barragemId)}/${flowId}/handles/${nodeId}`,
      payload
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Handle Error",
        message: error.response?.data.message ?? "Erro ao cadastrar handle",
      };
    }
    return {
      error: "Handle Error",
      message: "Erro ao cadastrar handle",
    };
  }
};

const updateHandle = async (
  barragemId: number,
  flowId: number,
  handleId: string,
  fields: {
    position?: HandlePosition;
    style?: React.CSSProperties;
  }
): Promise<Handle | RequestError> => {
  try {
    const { data } = await axios.put<Handle>(
      `${BASE_URL(barragemId)}/${flowId}/handles/${handleId}`,
      fields
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Handle Error",
        message: error.response?.data.message ?? "Erro ao atualizar handle",
      };
    }
    return {
      error: "Handle Error",
      message: "Erro ao atualizar handle",
    };
  }
};

const deleteHandle = async (
  barragemId: number,
  flowId: number,
  handleId: string
): Promise<Handle | RequestError> => {
  try {
    const { data } = await axios.delete<Handle>(
      `${BASE_URL(barragemId)}/${flowId}/handles/${handleId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Handle Error",
        message: error.response?.data.message ?? "Erro ao remover handle",
      };
    }
    return {
      error: "Handle Error",
      message: "Erro ao remover handle",
    };
  }
};

const getHandle = async (
  barragemId: number,
  flowId: number,
  handleId: string
): Promise<Handle | RequestError> => {
  try {
    const { data } = await axios.get<Handle>(
      `${BASE_URL(barragemId)}/${flowId}/handles/${handleId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Handle Error",
        message: error.response?.data.message ?? "Erro ao obter handle",
      };
    }
    return {
      error: "Handle Error",
      message: "Erro ao obter handle",
    };
  }
};

export const ApiFluxogramas = {
  getAll,
  addFlow,
  updateFlow,
  deleteFlow,
  getFlow,
  addNode,
  updateNode,
  deleteNode,
  getNode,
  addEdge,
  updateEdge,
  deleteEdge,
  getEdge,
  addHandle,
  updateHandle,
  deleteHandle,
  getHandle,
};
